import React, { useState } from 'react';
import { Tooltip, Text, View } from '@smarterservices/smarter-ui';
import KeyboardShortcutDisplay from '../KeyboardShortcutDisplay';
import { useFullScreenMountNode } from '../../proctoring/SessionViewer/contexts/FullScreenContext';

interface NavigationalToolTipProps {
  children: React.ReactNode;
  description: string;
  shortcut?: string; // Single shortcut string like "Ctrl+C" or "B"
  
  // Tooltip props (allow full customization)
  placement?: 'top' | 'bottom' | 'start' | 'end';
  color?: 'primary' | 'primary-inverse';
  offsetX?: number;
  offsetY?: number;
  on?: 'click' | 'hover' | 'focus' | string;
  isShowingContent?: boolean;
  onShowContent?: () => void;
  onHideContent?: () => void;
  
  // Content styling props
  maxWidth?: string;
  padding?: string;
  textAlign?: 'start' | 'center' | 'end';
  
  // Text styling props
  descriptionSize?: 'x-small' | 'small' | 'medium' | 'large' | 'x-large';
  shortcutTextVariant?: 'legend' | 'contentSmall' | 'contentImportant' | 'content' | 'descriptionSection';
  shortcutVariant?: 'light' | 'dark' | 'medium';
  showSeparator?: boolean;
  
  // Layout props
  showShortcutsFirst?: boolean;
  shortcutSpacing?: string;
  shortcutsOnNewLine?: boolean;
}

export function NavigationalToolTip({
  children,
  description,
  shortcut,
  
  // Tooltip props with defaults
  placement = 'bottom',
  color = 'primary',
  offsetX,
  offsetY,
  on = 'hover',
  isShowingContent,
  onShowContent,
  onHideContent,
  
  // Content styling with defaults
  maxWidth = '300px',
  textAlign = 'start',
  
  // Text styling with defaults
  descriptionSize = 'x-small',
  shortcutTextVariant = 'legend',
  shortcutVariant = 'light',
  showSeparator = false,
  
  // Layout options
  showShortcutsFirst = false,
  shortcutSpacing = 'x-small',
  shortcutsOnNewLine = true,
}: NavigationalToolTipProps) {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  
  // Get the appropriate mount node for full screen support
  const mountNode = useFullScreenMountNode();
  
  // Use controlled state if not provided
  const tooltipVisible = isShowingContent !== undefined ? isShowingContent : isTooltipVisible;
  const handleShowContent = onShowContent || (() => setIsTooltipVisible(true));
  const handleHideContent = onHideContent || (() => setIsTooltipVisible(false));
  
  const tooltipContent = (
    <View as="div" padding="none x-small" textAlign={textAlign} maxWidth={maxWidth || '300px'}>
      {shortcutsOnNewLine ? (
        // New line layout: description on first line, shortcuts centered on second line
        <>
          {description && (
              <Text size={descriptionSize}>
                {description}
              </Text>
          )}
          {shortcut && (
            <div style={{ textAlign: 'center' }}>
              {React.createElement(KeyboardShortcutDisplay, {
                shortcut,
                textVariant: shortcutTextVariant,
                margin: "none",
                variant: shortcutVariant,
                showSeparator
              })}
            </div>
          )}
        </>
      ) : (
        // Original inline layout
        <>
          {description && (
            <Text size={descriptionSize}>
              {description}
            </Text>
          )}
          {shortcut && (
            React.createElement(KeyboardShortcutDisplay, {
              shortcut,
              textVariant: shortcutTextVariant,
              margin: "none none none xx-small",
              variant: shortcutVariant,
              showSeparator
            })
          )}
        </>
      )}
    </View>
  );

  return (
    <Tooltip
      renderTip={tooltipContent}
      placement={placement}
      color={color}
      offsetX={offsetX}
      offsetY={offsetY}
      mountNode={mountNode}
      isShowingContent={isTooltipVisible}
      onShowContent={handleShowContent}
      onHideContent={handleHideContent}
      themeOverride={{
        padding: '.25rem'
      }}
    >
      <span
        onMouseEnter={handleShowContent}
        onMouseLeave={handleHideContent}
        style={{ display: 'inline-block' }}
      >
        {children}
      </span>
    </Tooltip>
  );
}

export default NavigationalToolTip;